import { lazy } from "react";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import AuthContextProvider from "./context/AuthContext";
import { useTenantColor } from "./hooks/useTenantColor";

// pages
import {
  ForgotPasswordPage,
  HomePage,
  StudentsPage,
  LessonsDetailsPage,
  LessonsPage,
  LoginPage,
  InstructorsPage,
  ProfilePage,
  SettingsPage,
  AssignmentSubmissions,
  LoginWithCodePage,
  SubjectsPage,
  StudentLessonPage,
  StudentAssignmentPage,
  LiveClassPage,
} from "./pages";
import SubjectLessonsPage from "./pages/SubjectLessonsPage";

// layouts
const MainLayout = lazy(() => import("./components/layouts/MainLayout"));
const AuthLayout = lazy(() => import("./components/layouts/AuthLayout"));

const AuthProvider = () => {
  return (
    <AuthContextProvider>
      <ColorProvider />
    </AuthContextProvider>
  );
};

const ColorProvider = () => {
  const { primary, primaryTransparent, primaryForeground } = useTenantColor();

  return (
    <div
      style={
        {
          "--color-primary": primary,
          "--color-primary-transparent": primaryTransparent,
          "--color-primary-foreground": primaryForeground,
        } as React.CSSProperties
      }
    >
      <Outlet />
      <Toaster position="top-right" />
    </div>
  );
};

const Router = createBrowserRouter([
  {
    path: "/",
    element: <AuthProvider />,
    children: [
      {
        path: "admin",
        children: [
          {
            path: "",
            element: <MainLayout />,
            children: [
              { index: true, element: <HomePage /> },
              {
                path: "lessons",
                handle: { crumb: "Lesson" },
                children: [
                  {
                    index: true,
                    element: <LessonsPage />,
                  },
                  {
                    path: ":subject_id/:class_id/:lesson_id",
                    handle: { crumb: "View Lesson" },
                    children: [
                      {
                        index: true,
                        element: <LessonsDetailsPage />,
                      },
                      {
                        handle: { crumb: "Assignment Submission" },
                        path: "assignment/:assignmentId",
                        element: <AssignmentSubmissions />,
                      },
                    ],
                  },
                ],
              },
              { path: "students", element: <StudentsPage /> },
              { path: "instructors", element: <InstructorsPage /> },
              { path: "profile", element: <ProfilePage /> },
              { path: "settings", element: <SettingsPage /> },
              {
                path: "*",
                element: (
                  <div>
                    <b className="text-destructive">404:</b> Not found
                  </div>
                ),
              },
            ],
          },
          {
            path: "",
            element: <AuthLayout />,
            children: [
              {
                path: "login",
                children: [
                  { index: true, element: <LoginPage /> },
                  { path: ":code", element: <LoginWithCodePage /> },
                ],
              },
              {
                path: "forgot-password",
                element: <ForgotPasswordPage />,
              },
            ],
          },
        ],
      },
      {
        path: "teacher",
        children: [
          {
            path: "",
            element: <MainLayout />,
            children: [
              { index: true, element: <HomePage /> },
              {
                path: "lessons",
                handle: { crumb: "Lesson" },
                children: [
                  {
                    index: true,
                    element: <LessonsPage />,
                  },
                  {
                    path: ":subject_id/:class_id/:lesson_id",
                    handle: { crumb: "View Lesson" },
                    children: [
                      {
                        index: true,
                        element: <LessonsDetailsPage />,
                      },
                      {
                        handle: { crumb: "Assignment Submission" },
                        path: "assignment/:assignmentId",
                        element: <AssignmentSubmissions />,
                      },
                    ],
                  },
                ],
              },
              { path: "profile", element: <ProfilePage /> },
              { path: "live-class", element: <LiveClassPage /> },
              {
                path: "subjects",
                handle: { crumb: "Subjects" },
                children: [
                  { index: true, element: <SubjectsPage /> },
                  {
                    path: ":class_id",
                    handle: { crumb: "View Lessons" },
                    element: <StudentsPage />
                  },
                  {
                    path: ":subject_id/:class_id",
                    handle: { crumb: "View Lessons" },
                    element: <SubjectLessonsPage />,
                  },
                ],
              },
              {
                path: "*",
                element: (
                  <div>
                    <b className="text-destructive">404:</b> Not found
                  </div>
                ),
              },
            ],
          },
          {
            path: "",
            element: <AuthLayout />,
            children: [
              {
                path: "login",
                children: [
                  { index: true, element: <LoginPage /> },
                  { path: ":code", element: <LoginWithCodePage /> },
                ],
              },
              {
                path: "forgot-password",
                element: <ForgotPasswordPage />,
              },
            ],
          },
        ],
      },
      {
        path: "",
        children: [
          {
            path: "",
            element: <MainLayout />,
            children: [
              { index: true, element: <HomePage /> },
              {
                path: "lessons",
                children: [
                  { index: true, element: <StudentLessonPage /> },
                  { path: ":subject_id/:class_id", element: <LessonsDetailsPage /> },
                  { path: ":subject_id/:class_id/:lesson_id", element: <LessonsDetailsPage /> },
                ],
              },
              { path: "assignments", element: <StudentAssignmentPage /> },
              { path: "profile", element: <ProfilePage /> },
              {
                path: "*",
                element: (
                  <div>
                    <b className="text-destructive">404:</b> Not found
                  </div>
                ),
              },
            ],
          },
          {
            path: "",
            element: <AuthLayout />,
            children: [
              {
                path: "login",
                children: [
                  { index: true, element: <LoginPage /> },
                  { path: ":code", element: <LoginWithCodePage /> },
                ],
              },
              {
                path: "forgot-password",
                element: <ForgotPasswordPage />,
              },
            ],
          },
          {
            path: "*",
            element: (
              <div>
                <b className="text-destructive">404:</b> Not found
              </div>
            ),
          },
        ],
      },
    ],
  },
]);

const RouterComponent = () => {
  return <RouterProvider router={Router} />;
};

export default RouterComponent;
